import { parse } from "query-string";
import React from "react";
import { MapStateToPropsParam } from "react-redux";
import { RouteComponentProps } from "react-router";

import ArticleSearch from "@/mk2/apps/wiki/containers/ArticleSearch/ArticleSearch";
import { articleSearchFetchTrigger } from "@/mk2/apps/wiki/containers/ArticleSearch/ArticleSearch.actions";
import articlesReducer from "@/mk2/apps/wiki/containers/ArticleSearch/ArticleSearch.reducers";
import articlesSaga from "@/mk2/apps/wiki/containers/ArticleSearch/ArticleSearch.sagas";
import { getArticleSearchState } from "@/mk2/apps/wiki/containers/ArticleSearch/ArticleSearch.selectors";
import { WIKI_REVIEWS_ARTICLE_SLUG } from "@/mk2/apps/wiki/helpers";
import { getWikiArticleUrl } from "@/mk2/apps/wiki/urls";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import Page, { HalfpageProps } from "@/mk2/pages/Page";
import { AppState, getRequestPermissions, getRequestUser } from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { UserEntity, WikiArticleEntity, WikiArticleSchema } from "@/mk2/schemas";
import { getDenormalizedEntities } from "@/mk2/selectors";
import { Store } from "@/mk2/store/configureStore";
import { SEO_TITLE_WIKI_REVIEWS_SEARCH } from "@/mk/autogenerated/translation";

export interface OwnProps {}

export interface StateProps {
    after: number;
    articles: WikiArticleEntity[];
    articleSlug: string;
    categorySlug: string;
    errorMessage: string;
    hasNext: number;
    hasPrevious: number;
    loadingState: LoadingState;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    resultsCount: number;
    searchQuery: string;
}

export interface DispatchProps {
    onLoad(articleSlug: string, categorySlug: string, search: string, after?: number);
}

export type RouteProps = RouteComponentProps<{
    articleSlug: string;
    categorySlug?: string;
}>;
export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class ArticleSearchPage extends Page<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const articleSlug = ownProps.match.params.articleSlug;
            const categorySlug = ownProps.match.params.categorySlug;
            const parsed = parse(ownProps.location.search, { parseNumbers: true });
            const after = parsed.after as number;
            const searchQuery = (parsed.q as string) || "";

            const searchState = getArticleSearchState(
                state,
                categorySlug,
                articleSlug,
                searchQuery,
            );

            const articles: WikiArticleEntity[] = getDenormalizedEntities<WikiArticleEntity>(
                state,
                WikiArticleSchema,
                searchState.ids,
            );

            return {
                after,
                articles,
                articleSlug,
                categorySlug,
                errorMessage: searchState.errorMessage,
                hasNext: searchState.hasNext,
                hasPrevious: searchState.hasPrevious,
                loadingState: searchState.loadingState,
                requestUser: getRequestUser(state),
                requestUserPermissions: getRequestPermissions(state),
                resultsCount: searchState.resultsCount,
                searchQuery,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: articleSearchFetchTrigger,
        };
    }

    public getMetaTitle(props: Props): string {
        return SEO_TITLE_WIKI_REVIEWS_SEARCH;
    }

    public render(props: Props): false | JSX.Element {
        return <ArticleSearch {...props} />;
    }

    public renderRight(props: Props): JSX.Element {
        return null;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        return getWikiArticleUrl(WIKI_REVIEWS_ARTICLE_SLUG, WIKI_REVIEWS_ARTICLE_SLUG);
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return null;
    }

    public getBrandingProps(props: Props): HalfpageProps {
        return null;
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.wiki.articleSearch", articlesReducer);
        store.injectSaga("containers.wiki.articleSearch", articlesSaga);
    }
}
